/**
 * Order Edit/Create Page
 *
 * View order details or manually create new orders.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { api } from '../../lib/api';

interface Store {
  id: string;
  name: string;
  currency: string;
}

interface OutletContext {
  store: Store;
}

interface Product {
  id: string;
  name: string;
  price: number;
  images: string[];
}

interface OrderItem {
  productId: string;
  productName: string;
  variantId?: string;
  variantName?: string;
  price: number;
  quantity: number;
}

interface Order {
  id: string;
  order_number: string;
  status: string;
  payment_status: string;
  customer_email: string;
  customer_name: string;
  customer_phone?: string;
  subtotal: number;
  total: number;
  currency: string;
  items: OrderItem[];
  shipping_address?: {
    address1: string;
    address2?: string;
    city: string;
    state?: string;
    postal_code: string;
    country: string;
  };
  internal_notes?: string;
  created_at: string;
}

export default function OrderEditPage() {
  const { storeId, orderId } = useParams<{ storeId: string; orderId: string }>();
  const navigate = useNavigate();
  const context = useOutletContext<OutletContext | null>();
  const store = context?.store;
  const effectiveStoreId = store?.id || storeId || '';

  const isNew = orderId === 'new';

  const [order, setOrder] = useState<Partial<Order>>({
    status: 'pending',
    payment_status: 'pending',
    customer_email: '',
    customer_name: '',
    items: [],
    currency: store?.currency || 'USD',
  });
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showProductPicker, setShowProductPicker] = useState(false);

  const loadOrder = useCallback(async () => {
    if (isNew) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(`/api/ecommerce/stores/${effectiveStoreId}/orders/${orderId}`);
      const data = await response.json() as { order: Order };
      if (data.order) {
        setOrder(data.order);
      }
    } catch {
      setError('Failed to load order');
    } finally {
      setLoading(false);
    }
  }, [effectiveStoreId, orderId, isNew]);

  const loadProducts = useCallback(async () => {
    try {
      const response = await api.get(`/api/ecommerce/stores/${effectiveStoreId}/products?status=active`);
      const data = await response.json() as { products: Product[] };
      setProducts(data.products || []);
    } catch {
      // Ignore
    }
  }, [effectiveStoreId]);

  useEffect(() => {
    loadOrder();
    if (isNew) {
      loadProducts();
    }
  }, [loadOrder, loadProducts, isNew]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: order.currency || 'USD',
    }).format(price / 100);
  };

  const handleAddItem = (product: Product) => {
    const items = [...(order.items || [])];
    const existingIndex = items.findIndex(item => item.productId === product.id);

    if (existingIndex >= 0) {
      items[existingIndex].quantity += 1;
    } else {
      items.push({
        productId: product.id,
        productName: product.name,
        price: product.price,
        quantity: 1,
      });
    }

    setOrder({ ...order, items });
    setShowProductPicker(false);
  };

  const handleRemoveItem = (index: number) => {
    const items = [...(order.items || [])];
    items.splice(index, 1);
    setOrder({ ...order, items });
  };

  const handleUpdateQuantity = (index: number, quantity: number) => {
    if (quantity < 1) return;
    const items = [...(order.items || [])];
    items[index].quantity = quantity;
    setOrder({ ...order, items });
  };

  const subtotal = (order.items || []).reduce((sum, item) => sum + item.price * item.quantity, 0);

  const handleSave = async () => {
    if (!order.customer_email || !order.customer_name) {
      setError('Customer name and email are required');
      return;
    }

    if ((order.items || []).length === 0) {
      setError('Please add at least one item');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (isNew) {
        const response = await api.post(`/api/ecommerce/stores/${effectiveStoreId}/orders`, {
          customer_email: order.customer_email,
          customer_name: order.customer_name,
          customer_phone: order.customer_phone,
          status: order.status,
          payment_status: order.payment_status,
          items: order.items,
          shipping_address: order.shipping_address,
          internal_notes: order.internal_notes,
        });
        const data = await response.json() as { order: Order };
        if (data.order) {
          navigate(`/store/${effectiveStoreId}/orders/${data.order.id}`, { replace: true });
        }
      } else {
        await api.put(`/api/ecommerce/stores/${effectiveStoreId}/orders/${orderId}`, {
          status: order.status,
          payment_status: order.payment_status,
          internal_notes: order.internal_notes,
        });
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save order');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/store/${effectiveStoreId}/orders`)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
          >
            <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? 'Create Order' : `Order ${order.order_number || ''}`}
            </h1>
            {!isNew && order.created_at && (
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Created {new Date(order.created_at).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : isNew ? 'Create Order' : 'Update Order'}
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400">
          Order saved successfully!
        </div>
      )}

      <div className="space-y-6">
        {/* Customer Info */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Customer Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Customer Name *
              </label>
              <input
                type="text"
                value={order.customer_name || ''}
                onChange={(e) => setOrder({ ...order, customer_name: e.target.value })}
                disabled={!isNew}
                className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email *
              </label>
              <input
                type="email"
                value={order.customer_email || ''}
                onChange={(e) => setOrder({ ...order, customer_email: e.target.value })}
                disabled={!isNew}
                className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={order.customer_phone || ''}
                onChange={(e) => setOrder({ ...order, customer_phone: e.target.value })}
                disabled={!isNew}
                className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
            </div>
          </div>
        </div>

        {/* Order Status */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Order Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Order Status
              </label>
              <select
                value={order.status || 'pending'}
                onChange={(e) => setOrder({ ...order, status: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
              >
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Payment Status
              </label>
              <select
                value={order.payment_status || 'pending'}
                onChange={(e) => setOrder({ ...order, payment_status: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
          </div>
        </div>

        {/* Order Items */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Order Items</h2>
            {isNew && (
              <button
                onClick={() => setShowProductPicker(true)}
                className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700"
              >
                + Add Product
              </button>
            )}
          </div>

          {(order.items || []).length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">
              No items added yet
            </p>
          ) : (
            <div className="space-y-3">
              {(order.items || []).map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{item.productName}</p>
                    {item.variantName && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">{item.variantName}</p>
                    )}
                    <p className="text-sm text-teal-600">{formatPrice(item.price)} each</p>
                  </div>
                  <div className="flex items-center gap-4">
                    {isNew ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleUpdateQuantity(index, item.quantity - 1)}
                          className="w-8 h-8 border border-gray-200 rounded flex items-center justify-center hover:bg-gray-100"
                        >
                          -
                        </button>
                        <span className="w-8 text-center">{item.quantity}</span>
                        <button
                          onClick={() => handleUpdateQuantity(index, item.quantity + 1)}
                          className="w-8 h-8 border border-gray-200 rounded flex items-center justify-center hover:bg-gray-100"
                        >
                          +
                        </button>
                        <button
                          onClick={() => handleRemoveItem(index)}
                          className="text-red-500 hover:text-red-700 ml-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-600 dark:text-gray-400">x{item.quantity}</span>
                    )}
                    <span className="font-medium text-gray-900 dark:text-white w-24 text-right">
                      {formatPrice(item.price * item.quantity)}
                    </span>
                  </div>
                </div>
              ))}
              <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-right">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Subtotal</p>
                  <p className="text-xl font-bold text-teal-600">{formatPrice(subtotal)}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Internal Notes */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Internal Notes</h2>
          <textarea
            value={order.internal_notes || ''}
            onChange={(e) => setOrder({ ...order, internal_notes: e.target.value })}
            placeholder="Add notes about this order (only visible to store admins)"
            rows={4}
            className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
          />
        </div>
      </div>

      {/* Product Picker Modal */}
      {showProductPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add Product</h3>
              <button onClick={() => setShowProductPicker(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-96">
              {products.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No products available</p>
              ) : (
                <div className="space-y-2">
                  {products.map(product => (
                    <button
                      key={product.id}
                      onClick={() => handleAddItem(product)}
                      className="w-full p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg flex items-center gap-3"
                    >
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex-shrink-0">
                        {product.images?.[0] && (
                          <img src={product.images[0]} alt="" className="w-full h-full object-cover rounded-lg" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">{product.name}</p>
                        <p className="text-sm text-teal-600">{formatPrice(product.price)}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
